import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Plus, Loader2, Pencil, Archive, RefreshCw, Shield, Trash2, Tag, Sparkles,
  Download, FileText, FileType, FileSpreadsheet, ChevronDown, Eye, X, Inbox, ClipboardList, Users,
} from 'lucide-react';
import { exportIncidentsPDF, exportIncidentsDocx, exportIncidentRegisterXlsx } from '../../utils/exportUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, Tabs, useUrlTab, DataTable, DetailDrawer, Dialog, Field, FormRow, Switch, KpiTile,
  SectionCard, EmptyState, ErrorState, TableSkeleton, OverflowMenu, SearchInput, FilterChips,
  StatusPill, PriorityPill, fmtDate, fmtDateTime, fmtTime, num, plural,
} from '../../components/ui';

// Incidents. The page opens on the queue — open, in-progress and escalated incidents plus any
// reportable incident the NDIS Commission hasn't been told about — ordered by how soon the
// Commission must be notified, then by priority. The analytics that used to be the first tab now
// live under Insights.

// ── Vocabulary ───────────────────────────────────────────────────────────────
const STATUSES = ['open', 'in_progress', 'escalated', 'resolved', 'closed'];
const OPEN_STATUSES = ['open', 'in_progress', 'escalated'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const PRIORITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', escalated: 'Escalated', resolved: 'Resolved', closed: 'Closed' };
const PRIORITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
const TYPES = ['fall', 'medication_error', 'behavior', 'property_damage', 'near_miss', 'abuse', 'elopement', 'medical_emergency'];
const TYPE_LABELS = {
  fall: 'Fall', medication_error: 'Medication error', behavior: 'Behaviour', property_damage: 'Property damage',
  near_miss: 'Near miss', abuse: 'Abuse or neglect', elopement: 'Elopement', medical_emergency: 'Medical emergency',
};
const typeLabel = (t) => (t ? TYPE_LABELS[t] || String(t).replace(/_/g, ' ') : '');

const TABS = [
  { id: 'queue', label: 'Queue' },
  { id: 'all', label: 'All incidents' },
  { id: 'ndis', label: 'NDIS reportable' },
  { id: 'insights', label: 'Insights' },
  { id: 'settings', label: 'Settings' },
];
const TAB_IDS = TABS.map((t) => t.id);
// Old links used tab numbers (?tab=7). They still land in the right place.
const LEGACY_TAB = { 0: 'insights', 1: 'all', 2: 'queue', 3: 'insights', 4: 'insights', 5: 'all', 6: 'insights', 7: 'ndis', 8: 'settings' };
const LEGACY_VIEW = { 3: 'client', 4: 'cumulative', 6: 'staff' };
const INSIGHT_VIEWS = [
  { id: 'overview', label: 'Overview' },
  { id: 'cumulative', label: 'Cumulative' },
  { id: 'staff', label: 'By staff' },
  { id: 'client', label: 'By client' },
];

// Category colours only tint the charts; on screen a category is a plain grey chip. Red, amber and
// green mean priority and risk everywhere, so they are no longer offered, and categories saved
// with them earlier are drawn in a neutral stand-in.
const CATEGORY_COLOURS = [
  { value: 'blue', label: 'Blue', hex: '#3b82f6' },
  { value: 'indigo', label: 'Indigo', hex: '#6366f1' },
  { value: 'purple', label: 'Purple', hex: '#8b5cf6' },
  { value: 'cyan', label: 'Cyan', hex: '#06b6d4' },
  { value: 'sky', label: 'Sky', hex: '#0ea5e9' },
  { value: 'pink', label: 'Pink', hex: '#ec4899' },
  { value: 'slate', label: 'Grey', hex: '#94a3b8' },
];
const LEGACY_COLOUR = { green: 'cyan', yellow: 'indigo', orange: 'sky', red: 'pink' };
const colourOf = (c) => CATEGORY_COLOURS.find((o) => o.value === (LEGACY_COLOUR[c] || c)) || CATEGORY_COLOURS[0];

const CHART = { grid: '#e2e8f0', tick: '#64748b', bar: '#94a3b8', ink: '#334155', open: '#f59e0b' };
const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 };

const ymd = (v) => (v ? String(v).split('T')[0] : '');
// Today as a local calendar day (toISOString() would give yesterday before 10 am in Australia).
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const HOUR = 3600000;
const DAY = 86400000;

function daysUntil(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd(v));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / DAY);
}

// ── NDIS Commission notification clock ──────────────────────────────────────
// Same rule the server uses when it stores notification_deadline (routes/incidents.js): 24 hours
// for death, serious injury, abuse or neglect, unlawful contact and sexual misconduct (Incident
// Rules s 20), 5 business days for the other reportable categories, including unauthorised
// restrictive practice (s 21), counted from the incident date and time. The rule
// keys on the reportable category, not the incident type, so an incident marked reportable
// without a category shows how old it is instead of a deadline the app can't know.
const URGENT_REPORTABLE = ['death', 'serious_injury', 'abuse_neglect', 'unlawful_physical_contact', 'unlawful_sexual_contact', 'sexual_misconduct'];

function incidentStart(inc) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd(inc && inc.incident_date));
  if (!m) return null;
  const [h, mi] = String((inc && inc.incident_time) || '').split(':').map(Number);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number.isFinite(h) ? h : 0, Number.isFinite(mi) ? mi : 0);
}
function addBusinessDays(from, n) {
  const d = new Date(from);
  let counted = 0;
  while (counted < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) counted += 1; }
  return d;
}
function ndisClock(inc, now) {
  if (!inc || !inc.ndis_reportable) return null;
  if (inc.reported_to_ndis || inc.commission_notified) return { state: 'notified' };
  const start = incidentStart(inc);
  const ageMs = start ? Math.max(0, now - start.getTime()) : null;
  const cat = inc.reportable_category;
  if (!cat || !start) return { state: 'pending', ageMs };
  const urgent = URGENT_REPORTABLE.includes(cat);
  const deadline = urgent ? new Date(start.getTime() + 24 * HOUR) : addBusinessDays(start, 5);
  const leftMs = deadline.getTime() - now;
  return { state: leftMs < 0 ? 'overdue' : 'due', deadline, leftMs, ageMs, window: urgent ? '24 hours' : '5 business days' };
}
const needsNotifying = (c) => !!c && c.state !== 'notified';

function spanText(ms) {
  const a = Math.abs(ms);
  if (a < HOUR) return `${Math.max(1, Math.round(a / 60000))} min`;
  if (a < 2 * DAY) return `${Math.round(a / HOUR)} h`;
  return plural(Math.floor(a / DAY), 'day');
}
function ageText(ms) {
  if (ms == null) return 'date not recorded';
  const d = Math.floor(ms / DAY);
  return d <= 0 ? 'less than a day old' : `${plural(d, 'day')} old`;
}

// Order for everything that needs action: Commission deadlines first (soonest first), then
// reportable incidents without a deadline (oldest first), then by priority and due date.
function byUrgency(now) {
  return (a, b) => {
    const ca = ndisClock(a, now); const cb = ndisClock(b, now);
    const g = (c) => (c && (c.state === 'overdue' || c.state === 'due') ? 0 : c && c.state === 'pending' ? 1 : 2);
    const ga = g(ca); const gb = g(cb);
    if (ga !== gb) return ga - gb;
    if (ga === 0) return ca.deadline - cb.deadline;
    if (ga === 1) return (cb.ageMs || 0) - (ca.ageMs || 0);
    const pr = (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0);
    if (pr) return pr;
    const da = ymd(a.due_date) || '9999'; const db = ymd(b.due_date) || '9999';
    if (da !== db) return da < db ? -1 : 1;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  };
}

// ── Small pieces ─────────────────────────────────────────────────────────────
function CategoryChip({ slug, categories }) {
  const cat = categories.find((c) => c.slug === slug);
  if (!cat) return <span className="text-slate-500">—</span>;
  return <span className="badge-gray whitespace-nowrap">{cat.name}</span>;
}
const catName = (slug, categories) => (categories.find((c) => c.slug === slug) || {}).name || '';

function ReportingCell({ inc, now }) {
  const c = ndisClock(inc, now);
  if (!c) return <span className="text-slate-500" aria-label="Not reportable">—</span>;
  if (c.state === 'notified') return <span className="badge-gray">Notified</span>;
  if (c.state === 'pending') {
    return (
      <div className="leading-tight">
        <span className="badge-yellow whitespace-nowrap">Not yet notified</span>
        <div className="text-[12px] text-slate-500 mt-1 whitespace-nowrap">{ageText(c.ageMs)}</div>
      </div>
    );
  }
  const overdue = c.state === 'overdue';
  return (
    <div className="leading-tight">
      <span className={clsx(overdue ? 'badge-red' : 'badge-yellow', 'whitespace-nowrap')}>
        {overdue ? `Overdue by ${spanText(c.leftMs)}` : `Notify within ${spanText(c.leftMs)}`}
      </span>
      <div className="text-[12px] text-slate-500 mt-1 whitespace-nowrap">Deadline {fmtDateTime(c.deadline)}</div>
    </div>
  );
}

// The deadline, in words, at the top of an incident.
function ClockBanner({ inc, now }) {
  const c = ndisClock(inc, now);
  if (!c) return null;
  if (c.state === 'notified') {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] text-slate-700">
        Reported to the NDIS Commission{inc.commission_reference ? ` · reference ${inc.commission_reference}` : ''}.
      </div>
    );
  }
  if (c.state === 'pending') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" role="status">
        <p className="text-[13px] font-semibold text-amber-800">Reportable, not yet notified · {ageText(c.ageMs)}</p>
        <p className="text-[12.5px] text-amber-800/90 mt-0.5">
          No reportable category is recorded, so the notification deadline (24 hours or 5 business days) can't be worked out here.
          Once the NDIS Commission has been told, switch on "Reported to NDIS".
        </p>
      </div>
    );
  }
  const overdue = c.state === 'overdue';
  return (
    <div className={clsx('rounded-xl border px-4 py-3', overdue ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50')} role="status">
      <p className={clsx('text-[13px] font-semibold', overdue ? 'text-rose-700' : 'text-amber-800')}>
        {overdue ? `Notification overdue by ${spanText(c.leftMs)}` : `Notify the NDIS Commission within ${spanText(c.leftMs)}`}
      </p>
      <p className={clsx('text-[12.5px] mt-0.5', overdue ? 'text-rose-700/90' : 'text-amber-800/90')}>
        Deadline {fmtDateTime(c.deadline)} — {c.window} from the incident.
      </p>
    </div>
  );
}

function DueCell({ inc }) {
  if (!inc.due_date) return <span className="text-slate-500">—</span>;
  const d = daysUntil(inc.due_date);
  const late = d != null && d < 0 && OPEN_STATUSES.includes(inc.status);
  return (
    <div className="leading-tight whitespace-nowrap">
      <div className="tabular-nums">{fmtDate(ymd(inc.due_date))}</div>
      {late && <div className="text-[12px] font-medium text-rose-600">Overdue</div>}
    </div>
  );
}

// ── Download menu (PDF, Word, Excel register) ────────────────────────────────
function DownloadMenu({ busy, onPdf, onDocx, onXlsx }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const item = (label, Icon, fn) => (
    <button type="button" role="menuitem" onClick={() => { setOpen(false); fn(); }}
      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 text-left transition-colors duration-150">
      <Icon size={15} className="text-slate-400 shrink-0" />{label}
    </button>
  );
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} disabled={busy} aria-haspopup="menu" aria-expanded={open}
        className="btn-secondary">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
        Download <ChevronDown size={13} className="text-slate-400" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1.5 z-30 w-60 bg-white border border-slate-200 rounded-xl shadow-card-deep py-1.5">
          <p className="px-3.5 pt-1 pb-2 text-[12px] text-slate-500 border-b border-slate-100 mb-1">Everything in All incidents, with its filters, oldest first</p>
          {item('PDF', FileText, onPdf)}
          {item('Word document (.docx)', FileType, onDocx)}
          {item('Excel register (.xlsx)', FileSpreadsheet, onXlsx)}
        </div>
      )}
    </div>
  );
}

// ── Category dialog (add / edit) ─────────────────────────────────────────────
function CategoryDialog({ category, onClose, onSaved }) {
  const isEdit = !!(category && category.id);
  const [form, setForm] = useState({
    name: (category && category.name) || '',
    description: (category && category.description) || '',
    color: colourOf((category && category.color) || 'blue').value,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!form.name.trim()) { setError('Enter a category name.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/incidents/categories/${category.id}`, form); toast.success('Category updated'); }
      else { await api.post('/incidents/categories', form); toast.success('Category created'); }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not save the category');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} size="sm" title={isEdit ? 'Edit category' : 'New category'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="incident-category-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Create category'}
          </button>
        </>
      )}>
      <form id="incident-category-form" onSubmit={save} className="space-y-4">
        <Field label="Name" required error={error}>
          {(id) => (
            <input id={id} className="input-field" value={form.name} placeholder="For example, Medication incidents"
              onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); if (error) setError(''); }} />
          )}
        </Field>
        <Field label="Description">
          {(id) => (
            <textarea id={id} rows={2} className="input-field resize-none" value={form.description} placeholder="Optional"
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          )}
        </Field>
        <fieldset>
          <legend className="text-[13px] font-medium text-slate-700 mb-1">Chart colour</legend>
          <p className="text-[12px] text-slate-500 mb-2">Red, amber and green are kept for priority, so they aren't offered.</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_COLOURS.map((c) => {
              const on = form.color === c.value;
              return (
                <button key={c.value} type="button" aria-pressed={on} onClick={() => setForm((f) => ({ ...f, color: c.value }))}
                  className={clsx('inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors duration-150',
                    on ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50')}>
                  <span className="w-3 h-3 rounded-full" style={{ background: c.hex }} aria-hidden />{c.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}

// ── Create / edit form ───────────────────────────────────────────────────────
function SwitchRow({ label, hint, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-slate-800">{label}</p>
        {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} className="mt-0.5" />
    </div>
  );
}

function PeoplePicker({ label, people, selected, onToggle }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? people.filter((p) => `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(s)) : people;
  }, [people, q]);
  return (
    <fieldset className="min-w-0">
      <legend className="text-[13px] font-medium text-slate-700 mb-1">
        {label}{selected.length > 0 && <span className="text-slate-500 font-normal"> · {num(selected.length)} selected</span>}
      </legend>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        {people.length > 8 && (
          <div className="p-2 border-b border-slate-100"><SearchInput value={q} onChange={setQ} placeholder={`Find ${label.toLowerCase()}`} size="sm" /></div>
        )}
        <div className="max-h-40 overflow-y-auto p-1.5">
          {shown.length === 0 ? <p className="text-[12.5px] text-slate-500 px-2 py-2">Nobody to show</p> : shown.map((p) => (
            <label key={p.id} className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => onToggle(p.id)} className="rounded" />
              <span className="text-[13px] text-slate-700">{p.first_name} {p.last_name}</span>
            </label>
          ))}
        </div>
      </div>
    </fieldset>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="space-y-3.5">
      <h3 className="text-[13px] font-semibold text-slate-900 pb-2 border-b border-slate-100">{title}</h3>
      {children}
    </section>
  );
}

function IncidentForm({ incident, categories, onClose, onSaved }) {
  const confirm = useConfirm();
  const isEdit = !!(incident && incident.id);
  const [form, setForm] = useState(() => ({
    ticketName:        (incident && incident.ticket_name) || '',
    status:            (incident && incident.status) || 'open',
    priority:          (incident && incident.priority) || 'medium',
    category:          (incident && incident.category) || (categories[0] && categories[0].slug) || '',
    assigneeId:        (incident && incident.assignee_id) || '',
    dueDate:           ymd(incident && incident.due_date),
    ndisReportable:    (incident && incident.ndis_reportable) || false,
    reportedToNdis:    (incident && incident.reported_to_ndis) || false,
    isPrivate:         (incident && incident.is_private) || false,
    incidentDate:      ymd(incident && incident.incident_date) || todayYmd(),
    incidentTime:      (incident && incident.incident_time) || '',
    incidentType:      (incident && incident.incident_type) || '',
    location:          (incident && incident.location) || '',
    description:       (incident && incident.description) || '',
    actionsTaken:      (incident && incident.actions_taken) || '',
    witnesses:         (incident && incident.witnesses) || '',
    injuryDetails:     (incident && incident.injury_details) || '',
    followUpRequired:  (incident && incident.follow_up_required) || false,
    followUpNotes:     (incident && incident.follow_up_notes) || '',
    resolutionNotes:   (incident && incident.resolution_notes) || '',
    formCompleted:     (incident && incident.form_completed) || false,
    furtherAction:     (incident && incident.further_action) || '',
    preventRecurrence: (incident && incident.prevent_recurrence) || '',
    involvedStaffIds:  ((incident && incident.involved_staff) || []).map((s) => s.id),
    involvedClientIds: ((incident && incident.involved_clients) || []).map((c) => c.id),
    clientId:          (incident && incident.client_id) || '',
    staffId:           (incident && incident.staff_id) || '',
  }));
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiWriting, setAiWriting] = useState(false);
  const [titleError, setTitleError] = useState('');
  const dirtyRef = useRef(false);
  const titleRef = useRef(null);

  const set = (k, v) => { dirtyRef.current = true; setForm((f) => ({ ...f, [k]: v })); };
  const toggleArr = (key, id) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  };

  useEffect(() => {
    api.get('/staff').then((r) => setUsers(Array.isArray(r.data) ? r.data : ((r.data && r.data.staff) || []))).catch(() => {});
    api.get('/clients').then((r) => setClients(Array.isArray(r.data) ? r.data : ((r.data && r.data.clients) || []))).catch(() => {});
  }, []);

  // The drawer focuses itself when it opens; a new report then starts in the title.
  useEffect(() => {
    if (isEdit) return undefined;
    const t = setTimeout(() => { if (titleRef.current) titleRef.current.focus(); }, 40);
    return () => clearTimeout(t);
  }, [isEdit]);

  // Closing (Escape, the backdrop, Cancel) never throws away typed work without asking.
  const requestClose = useCallback(async () => {
    if (dirtyRef.current && !(await confirm('What you have typed in this incident will be lost.', {
      title: 'Discard changes?', confirmLabel: 'Discard changes', cancelLabel: 'Keep editing', danger: true,
    }))) return;
    onClose();
  }, [confirm, onClose]);

  // AI reads the worker's raw description and drafts every register field for the office to
  // review — type, category, priority, actions, prevention, injury, witnesses, reportability.
  // Nothing is saved; the admin reviews and saves.
  const genNdisReport = async () => {
    if (!form.description || !form.description.trim()) { toast.error('Write what happened first'); return; }
    setAiWriting(true);
    const tId = toast.loading('Drafting the incident register…');
    try {
      const cl = clients.find((c) => c.id === form.clientId);
      const stf = users.find((u) => u.id === (form.staffId || form.assigneeId));
      const { data } = await api.post('/ai/incident-autofill', {
        ticketName: form.ticketName, incidentType: form.incidentType,
        incidentDate: form.incidentDate, incidentTime: form.incidentTime,
        location: form.location, description: form.description, actionsTaken: form.actionsTaken,
        categories: (categories || []).map((c) => ({ slug: c.slug, name: c.name })),
        clientName: cl ? (cl.name || `${cl.first_name || ''} ${cl.last_name || ''}`.trim()) : '',
        staffName: stf ? (stf.name || `${stf.first_name || ''} ${stf.last_name || ''}`.trim()) : '',
      });
      dirtyRef.current = true;
      setForm((f) => ({
        ...f,
        description:       data.description || f.description,
        incidentType:      data.incidentType || f.incidentType,
        category:          data.category || f.category,
        priority:          data.priority || f.priority,
        actionsTaken:      data.immediateActionsTaken || f.actionsTaken,
        furtherAction:     data.furtherAction || f.furtherAction,
        preventRecurrence: data.preventRecurrence || f.preventRecurrence,
        injuryDetails:     data.injuryDetails || f.injuryDetails,
        witnesses:         data.witnesses || f.witnesses,
        ndisReportable:    data.ndisReportable === true ? true : f.ndisReportable,
        followUpRequired:  data.followUpRequired === true ? true : f.followUpRequired,
      }));
      toast.success('Draft ready. Check every field before you save.', { id: tId });
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'The AI draft failed', { id: tId });
    } finally { setAiWriting(false); }
  };

  const handleSave = async () => {
    if (!form.ticketName.trim()) {
      setTitleError('Give the incident a short title.');
      if (titleRef.current) { titleRef.current.focus(); titleRef.current.scrollIntoView({ block: 'center' }); }
      return;
    }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/incidents/${incident.id}`, form); toast.success('Incident updated'); }
      else { await api.post('/incidents', form); toast.success('Incident reported'); }
      dirtyRef.current = false;
      onSaved();
      onClose();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'Could not save the incident');
    } finally { setSaving(false); }
  };

  const activeCategories = categories.filter((c) => c.is_active || c.slug === form.category);

  return (
    <DetailDrawer open onClose={requestClose} width="xl"
      title={isEdit ? 'Edit incident' : 'Report incident'}
      subtitle={isEdit ? (incident.ticket_name || 'Incident') : 'Record what happened, who was involved and what happens next'}
      footer={(
        <>
          <button type="button" onClick={requestClose} className="btn-secondary">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Save incident'}
          </button>
        </>
      )}>
      <div className="space-y-8 pb-2">
        <FormSection title="What happened">
          <Field label="Title" required error={titleError}>
            {(id) => (
              <input id={id} ref={titleRef} className="input-field" value={form.ticketName} placeholder="A short description of the incident"
                aria-invalid={!!titleError}
                onChange={(e) => { set('ticketName', e.target.value); if (titleError) setTitleError(''); }} />
            )}
          </Field>
          <FormRow>
            <Field label="Category">
              {(id) => (
                <select id={id} className="input-field" value={form.category} onChange={(e) => set('category', e.target.value)}>
                  {activeCategories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Incident type">
              {(id) => (
                <select id={id} className="input-field" value={form.incidentType} onChange={(e) => set('incidentType', e.target.value)}>
                  <option value="">Choose a type</option>
                  {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              )}
            </Field>
            <Field label="Date of incident">
              {(id) => <input id={id} type="date" className="input-field" value={form.incidentDate} onChange={(e) => set('incidentDate', e.target.value)} />}
            </Field>
            <Field label="Time">
              {(id) => <input id={id} type="time" className="input-field" value={form.incidentTime} onChange={(e) => set('incidentTime', e.target.value)} />}
            </Field>
          </FormRow>
          <Field label="Location">
            {(id) => <input id={id} className="input-field" value={form.location} placeholder="Where did it happen?" onChange={(e) => set('location', e.target.value)} />}
          </Field>
          <Field label="Description"
            hint="Describe what happened in plain words. Auto-fill drafts the other fields from it for you to check."
            action={(
              <button type="button" onClick={genNdisReport} disabled={aiWriting} className="btn-secondary btn-sm">
                {aiWriting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}Auto-fill with AI
              </button>
            )}>
            {(id) => <textarea id={id} rows={4} className="input-field resize-y" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What happened, in order" />}
          </Field>
          <Field label="Immediate actions taken">
            {(id) => <textarea id={id} rows={2} className="input-field resize-y" value={form.actionsTaken} onChange={(e) => set('actionsTaken', e.target.value)} placeholder="What was done straight away" />}
          </Field>
          <FormRow>
            <Field label="Injury details">
              {(id) => <input id={id} className="input-field" value={form.injuryDetails} onChange={(e) => set('injuryDetails', e.target.value)} placeholder="Any injuries" />}
            </Field>
            <Field label="Witnesses">
              {(id) => <input id={id} className="input-field" value={form.witnesses} onChange={(e) => set('witnesses', e.target.value)} placeholder="Names of witnesses" />}
            </Field>
          </FormRow>
        </FormSection>

        <FormSection title="People involved">
          <FormRow>
            <Field label="Primary client">
              {(id) => (
                <select id={id} className="input-field" value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
                  <option value="">None</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Primary staff member">
              {(id) => (
                <select id={id} className="input-field" value={form.staffId} onChange={(e) => set('staffId', e.target.value)}>
                  <option value="">None</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              )}
            </Field>
          </FormRow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PeoplePicker label="Involved staff" people={users} selected={form.involvedStaffIds} onToggle={(id) => toggleArr('involvedStaffIds', id)} />
            <PeoplePicker label="Associated clients" people={clients} selected={form.involvedClientIds} onToggle={(id) => toggleArr('involvedClientIds', id)} />
          </div>
        </FormSection>

        <FormSection title="Reporting and follow-up">
          <FormRow>
            <Field label="Status">
              {(id) => (
                <select id={id} className="input-field" value={form.status} onChange={(e) => set('status', e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              )}
            </Field>
            <Field label="Priority" hint={!isEdit ? 'High and critical incidents are escalated when saved.' : undefined}>
              {(id) => (
                <select id={id} className="input-field" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                </select>
              )}
            </Field>
            <Field label="Assigned to">
              {(id) => (
                <select id={id} className="input-field" value={form.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Follow-up due">
              {(id) => <input id={id} type="date" className="input-field" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />}
            </Field>
          </FormRow>

          <div className="rounded-xl border border-slate-200 px-4 divide-y divide-slate-100">
            <SwitchRow label="NDIS reportable" hint="Reportable incidents must be notified to the NDIS Commission."
              checked={form.ndisReportable} onChange={(v) => set('ndisReportable', v)} />
            <SwitchRow label="Reported to NDIS" hint="Switch on once the NDIS Commission has been notified."
              checked={form.reportedToNdis} onChange={(v) => set('reportedToNdis', v)} />
            <SwitchRow label="Incident form completed" checked={form.formCompleted} onChange={(v) => set('formCompleted', v)} />
            <SwitchRow label="Private" checked={form.isPrivate} onChange={(v) => set('isPrivate', v)} />
            <SwitchRow label="Follow-up required" checked={form.followUpRequired} onChange={(v) => set('followUpRequired', v)} />
          </div>

          {form.followUpRequired && (
            <Field label="Follow-up notes">
              {(id) => <textarea id={id} rows={2} className="input-field resize-y" value={form.followUpNotes} onChange={(e) => set('followUpNotes', e.target.value)} />}
            </Field>
          )}
          <Field label="Further action or support">
            {(id) => <textarea id={id} rows={2} className="input-field resize-y" value={form.furtherAction} onChange={(e) => set('furtherAction', e.target.value)} placeholder="Further action or support arranged" />}
          </Field>
          <Field label="Actions to prevent recurrence">
            {(id) => <textarea id={id} rows={2} className="input-field resize-y" value={form.preventRecurrence} onChange={(e) => set('preventRecurrence', e.target.value)} placeholder="What will stop this happening again" />}
          </Field>
          {['resolved', 'closed'].includes(form.status) && (
            <Field label="Resolution notes">
              {(id) => <textarea id={id} rows={2} className="input-field resize-y" value={form.resolutionNotes} onChange={(e) => set('resolutionNotes', e.target.value)} placeholder="How it was resolved" />}
            </Field>
          )}
        </FormSection>
      </div>
    </DetailDrawer>
  );
}

// ── Incident detail (read-only) ──────────────────────────────────────────────
function DetailBlock({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div>
      <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1">{label}</h4>
      <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words">{children}</p>
    </div>
  );
}

function IncidentDrawer({ incident, categories, now, onClose, onEdit, onArchive }) {
  const inc = incident;
  const flags = [
    ['NDIS reportable', inc.ndis_reportable],
    ['Reported to NDIS', inc.reported_to_ndis],
    ['Incident form completed', inc.form_completed],
    ['Private', inc.is_private],
    ['Follow-up required', inc.follow_up_required],
  ];
  return (
    <DetailDrawer open onClose={onClose} width="lg" icon={AlertTriangle}
      title={inc.ticket_name || typeLabel(inc.incident_type) || 'Incident'}
      subtitle={[inc.ref_no ? `Ref ${inc.ref_no}` : null, inc.created_by_name ? `Logged by ${inc.created_by_name}` : null].filter(Boolean).join(' · ') || undefined}
      statusNode={<span className="flex items-center gap-1.5"><StatusPill status={inc.status} /><PriorityPill priority={inc.priority} /></span>}
      meta={[
        { label: 'Date', value: inc.incident_date ? fmtDate(ymd(inc.incident_date)) : '' },
        { label: 'Time', value: inc.incident_time ? fmtTime(inc.incident_time) : '' },
        { label: 'Category', value: catName(inc.category, categories) },
        { label: 'Type', value: typeLabel(inc.incident_type) },
        { label: 'Client', value: inc.client_name },
        { label: 'Staff', value: inc.staff_name },
        { label: 'Assigned to', value: inc.assignee_name || 'Unassigned' },
        { label: 'Location', value: inc.location },
        { label: 'Follow-up due', value: inc.due_date ? fmtDate(ymd(inc.due_date)) : '' },
      ]}
      footer={(
        <>
          <button type="button" onClick={onArchive} className="btn-ghost text-rose-600 hover:bg-rose-50 hover:text-rose-700 mr-auto">
            <Archive size={15} /> Archive
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
          <button type="button" onClick={onEdit} className="btn-primary"><Pencil size={15} /> Edit incident</button>
        </>
      )}>
      <div className="space-y-5">
        <ClockBanner inc={inc} now={now} />
        <DetailBlock label="What happened">{inc.description}</DetailBlock>
        <DetailBlock label="Immediate actions taken">{inc.actions_taken}</DetailBlock>
        <DetailBlock label="Injury details">{inc.injury_details}</DetailBlock>
        <DetailBlock label="Witnesses">{inc.witnesses}</DetailBlock>
        <DetailBlock label="Further action or support">{inc.further_action}</DetailBlock>
        <DetailBlock label="Actions to prevent recurrence">{inc.prevent_recurrence}</DetailBlock>
        {inc.follow_up_required && <DetailBlock label="Follow-up notes">{inc.follow_up_notes}</DetailBlock>}
        <DetailBlock label="Resolution notes">{inc.resolution_notes}</DetailBlock>

        {((inc.involved_staff && inc.involved_staff.length > 0) || (inc.involved_clients && inc.involved_clients.length > 0)) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {inc.involved_staff && inc.involved_staff.length > 0 && (
              <div>
                <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Involved staff</h4>
                <div className="flex flex-wrap gap-1.5">{inc.involved_staff.map((s) => <span key={s.id} className="badge-gray">{s.name}</span>)}</div>
              </div>
            )}
            {inc.involved_clients && inc.involved_clients.length > 0 && (
              <div>
                <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Associated clients</h4>
                <div className="flex flex-wrap gap-1.5">{inc.involved_clients.map((c) => <span key={c.id} className="badge-gray">{c.name}</span>)}</div>
              </div>
            )}
          </div>
        )}

        <div>
          <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Reporting</h4>
          <dl className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            {flags.map(([label, on]) => (
              <div key={label} className="flex items-center justify-between px-4 py-2 text-[13px]">
                <dt className="text-slate-600">{label}</dt>
                <dd className={clsx('font-medium', on ? 'text-slate-900' : 'text-slate-500')}>{on ? 'Yes' : 'No'}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </DetailDrawer>
  );
}

// ── The incidents table (queue, all incidents, by client) ────────────────────
function incidentColumns({ categories, now, onOpen, onEdit, onArchive, showReporting = true, showClient = true }) {
  return [
    {
      key: 'ticket_name', header: 'Incident', sortable: true, sortValue: (r) => String(r.ticket_name || '').toLowerCase(),
      render: (r) => (
        <div className="min-w-[13rem] max-w-[22rem]">
          <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(r); }}
            className="block max-w-full truncate text-left font-medium text-slate-900 hover:text-primary-700 hover:underline underline-offset-2">
            {r.ticket_name || typeLabel(r.incident_type) || 'Untitled incident'}
          </button>
          <div className="text-[12px] text-slate-500 truncate">
            {[catName(r.category, categories), typeLabel(r.incident_type), r.is_private ? 'Private' : null].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      ),
    },
    showClient && {
      key: 'client_name', header: 'Client', sortable: true,
      render: (r) => (
        <div className="leading-tight whitespace-nowrap">
          <div className="text-slate-800">{r.client_name || '—'}</div>
          {r.staff_name && <div className="text-[12px] text-slate-500">{r.staff_name}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', sortable: true, render: (r) => <StatusPill status={r.status} /> },
    {
      key: 'priority', header: 'Priority', sortable: true, defaultDir: 'desc',
      sortValue: (r) => PRIORITY_RANK[r.priority] || 0, render: (r) => <PriorityPill priority={r.priority} />,
    },
    showReporting && {
      key: 'reporting', header: 'NDIS reporting', sortable: true,
      sortValue: (r) => { const c = ndisClock(r, now); return !c ? null : c.state === 'notified' ? 9e15 : c.deadline ? c.deadline.getTime() : 1e15 - (c.ageMs || 0); },
      render: (r) => <ReportingCell inc={r} now={now} />,
    },
    {
      key: 'incident_date', header: 'Date', sortable: true, defaultDir: 'desc', sortValue: (r) => ymd(r.incident_date) || null,
      render: (r) => <span className="whitespace-nowrap tabular-nums">{r.incident_date ? fmtDate(ymd(r.incident_date)) : '—'}</span>,
    },
    { key: 'due_date', header: 'Due', sortable: true, sortValue: (r) => ymd(r.due_date) || null, render: (r) => <DueCell inc={r} /> },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: '3.5rem',
      render: (r) => (
        <OverflowMenu label={`Actions for ${r.ticket_name || 'incident'}`} size={16} className="p-2.5" items={[
          { label: 'View details', icon: Eye, onClick: () => onOpen(r) },
          { label: 'Edit incident', icon: Pencil, onClick: () => onEdit(r) },
          { divider: true },
          { label: 'Archive', icon: Archive, danger: true, onClick: () => onArchive(r) },
        ]} />
      ),
    },
  ].filter(Boolean);
}

// ── Queue ────────────────────────────────────────────────────────────────────
function NotifyTile({ loading, failed, notifyStats }) {
  return (
    <KpiTile label="Reportable, not yet notified" icon={Shield}
      value={loading ? '…' : failed ? '—' : num(notifyStats.count)}
      alert={!loading && !failed && notifyStats.count > 0}
      sub={failed ? "Couldn't load reportable incidents" : notifyStats.count > 0 ? `Oldest ${notifyStats.oldestText}` : 'Nothing waiting on the NDIS Commission'} />
  );
}

function QueueTab({ rows, loading, error, onRetry, notifyStats, ndisFailed, categories, now, handlers, onNew }) {
  const openCount = rows.filter((r) => OPEN_STATUSES.includes(r.status)).length;
  const escalated = rows.filter((r) => r.status === 'escalated').length;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiTile label="Open incidents" value={loading ? '…' : num(openCount)} icon={Inbox} alert={!loading && openCount > 0} sub="Open, in progress or escalated" />
        <KpiTile label="Escalated" value={loading ? '…' : num(escalated)} icon={AlertTriangle} accent="slate" />
        <div className="col-span-2 lg:col-span-1">
          <NotifyTile loading={loading} failed={ndisFailed} notifyStats={notifyStats} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-[14px] font-semibold text-slate-900">Needs action</h2>
          <p className="text-[12.5px] text-slate-500">Soonest NDIS deadline first, then by priority</p>
        </div>
        <DataTable
          label="Incidents that need action"
          columns={incidentColumns({ categories, now, ...handlers })}
          rows={rows}
          loading={loading}
          error={error}
          onRetry={onRetry}
          stickyHeader={false}
          onRowClick={handlers.onOpen}
          empty={{
            icon: ClipboardList,
            title: 'Nothing needs action',
            hint: 'Open, in-progress and escalated incidents, and reportable incidents not yet notified, appear here.',
            action: <button type="button" onClick={onNew} className="btn-secondary btn-sm"><Plus size={14} /> Report incident</button>,
          }}
        />
      </div>
    </div>
  );
}

// ── NDIS reportable ──────────────────────────────────────────────────────────
function NdisTab({ rows, loading, error, onRetry, ndisStats, notifyStats, now, handlers }) {
  const [checking, setChecking] = useState({});
  const [aiResults, setAiResults] = useState({});

  const checkReportability = async (inc) => {
    setChecking((p) => ({ ...p, [inc.id]: true }));
    try {
      const { data } = await api.post('/ai/ndis-reportability', {
        incidentType: inc.incident_type, description: inc.description, priority: inc.priority,
      });
      setAiResults((p) => ({ ...p, [inc.id]: data }));
    } catch { toast.error('The AI check failed. Try again.'); }
    finally { setChecking((p) => ({ ...p, [inc.id]: false })); }
  };

  const columns = [
    {
      key: 'ticket_name', header: 'Incident',
      render: (r) => (
        <div className="min-w-[12rem] max-w-[20rem]">
          <button type="button" onClick={(e) => { e.stopPropagation(); handlers.onOpen(r); }}
            className="block max-w-full truncate text-left font-medium text-slate-900 hover:text-primary-700 hover:underline underline-offset-2">
            {r.ticket_name || typeLabel(r.incident_type) || 'Untitled incident'}
          </button>
          <div className="text-[12px] text-slate-500 truncate">{typeLabel(r.incident_type) || '—'}</div>
        </div>
      ),
    },
    { key: 'client_name', header: 'Client', sortable: true, render: (r) => <span className="whitespace-nowrap">{r.client_name || '—'}</span> },
    {
      key: 'incident_date', header: 'Date', sortable: true, sortValue: (r) => ymd(r.incident_date) || null,
      render: (r) => <span className="whitespace-nowrap tabular-nums">{r.incident_date ? fmtDate(ymd(r.incident_date)) : '—'}</span>,
    },
    { key: 'priority', header: 'Priority', sortable: true, sortValue: (r) => PRIORITY_RANK[r.priority] || 0, render: (r) => <PriorityPill priority={r.priority} /> },
    { key: 'status', header: 'Status', sortable: true, render: (r) => <StatusPill status={r.status} /> },
    { key: 'reporting', header: 'Notification', render: (r) => <ReportingCell inc={r} now={now} /> },
    {
      key: 'ai', header: 'AI check',
      render: (r) => {
        const ai = aiResults[r.id];
        if (ai) {
          return (
            <div className="text-[12px] max-w-[14rem]">
              <span className={ai.reportable ? 'badge-red' : 'badge-green'}>{ai.reportable ? 'Reportable' : 'Not reportable'}</span>
              {ai.reason && <p className="text-slate-600 mt-1 leading-snug line-clamp-3" title={ai.reason}>{ai.reason}</p>}
            </div>
          );
        }
        return (
          <button type="button" onClick={(e) => { e.stopPropagation(); checkReportability(r); }} disabled={checking[r.id]} className="btn-secondary btn-sm">
            {checking[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}Check with AI
          </button>
        );
      },
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: '3.5rem',
      render: (r) => (
        <OverflowMenu label={`Actions for ${r.ticket_name || 'incident'}`} size={16} className="p-2.5" items={[
          { label: 'View details', icon: Eye, onClick: () => handlers.onOpen(r) },
          { label: 'Edit incident', icon: Pencil, onClick: () => handlers.onEdit(r) },
        ]} />
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="col-span-2 lg:col-span-1">
          <NotifyTile loading={loading} failed={!!error} notifyStats={notifyStats} />
        </div>
        <KpiTile label="Reported to NDIS" value={ndisStats ? num(Number(ndisStats.reported) || 0) : '…'} accent="slate" />
        <KpiTile label="Total reportable" value={ndisStats ? num(Number(ndisStats.to_be_reported) || 0) : '…'} accent="slate" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-[14px] font-semibold text-slate-900">NDIS reportable incidents</h2>
          <p className="text-[12.5px] text-slate-500">Not yet notified first, soonest deadline first</p>
        </div>
        <DataTable
          label="NDIS reportable incidents"
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          onRetry={onRetry}
          stickyHeader={false}
          onRowClick={handlers.onOpen}
          empty={{ icon: Shield, title: 'No NDIS reportable incidents', hint: 'Incidents marked "NDIS reportable" appear here with their notification deadline.' }}
        />
      </div>
    </div>
  );
}

// ── Insights ─────────────────────────────────────────────────────────────────
function Leaderboard({ title, rows, empty }) {
  const max = rows.length ? Math.max(...rows.map((r) => Number(r.count) || 0), 1) : 1;
  return (
    <SectionCard title={title} bodyClass="p-5">
      {rows.length === 0 ? <p className="text-[13px] text-slate-500">{empty}</p> : (
        <ol className="space-y-3">
          {rows.map((r, i) => (
            <li key={`${r.name}-${i}`} className="flex items-center gap-3">
              <span className="w-5 text-[12px] font-semibold text-slate-500 tabular-nums text-right shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2 mb-1">
                  <span className="text-[13px] text-slate-700 truncate">{r.name}</span>
                  <span className="text-[13px] font-semibold text-slate-900 tabular-nums">{num(Number(r.count) || 0)}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full">
                  <div className="h-1.5 bg-slate-400 rounded-full" style={{ width: `${Math.min(100, ((Number(r.count) || 0) / max) * 100)}%` }} />
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}

function OverviewSection({ stats, categories }) {
  const { overview = [], overTime = [], resRate, closeTime = [], byStaff = [], byClient = [], totals = {} } = stats;
  const resPercent = resRate && Number(resRate.total) > 0 ? Math.round((Number(resRate.resolved) / Number(resRate.total)) * 100) : 0;
  const openTotal = Number(totals.open_total) || 0;
  const sum = (k) => overview.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const catLabel = (slug) => catName(slug, categories) || slug;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Total incidents" value={num(Number(totals.total) || 0)} accent="slate" />
        <KpiTile label="Open" value={num(openTotal)} alert={openTotal > 0} />
        <KpiTile label="Escalated" value={num(Number(totals.escalated_total) || 0)} accent="slate" />
        <KpiTile label="Resolution rate" value={`${resPercent}%`} sub="Last 30 days" accent="slate" />
      </div>

      <SectionCard title="Incidents by category">
        {/* Phones: one line per category, the other periods behind a disclosure. */}
        <ul className="sm:hidden divide-y divide-slate-100">
          {overview.map((row) => (
            <li key={row.category} className="px-4 py-3">
              <details className="group">
                <summary className="flex items-center justify-between gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <span className="text-[13px] font-medium text-slate-800">{catLabel(row.category)}</span>
                  <span className="flex items-center gap-2 text-[13px] tabular-nums text-slate-600">
                    <span><span className="font-semibold text-slate-900">{num(Number(row.open_count) || 0)}</span> open</span>
                    <ChevronDown size={14} className="text-slate-400 transition-transform duration-150 group-open:rotate-180" aria-hidden />
                  </span>
                </summary>
                <dl className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
                  <div><dt className="text-slate-500">Today</dt><dd className="tabular-nums text-slate-800">{num(Number(row.new_today) || 0)}</dd></div>
                  <div><dt className="text-slate-500">This week</dt><dd className="tabular-nums text-slate-800">{num(Number(row.new_this_week) || 0)}</dd></div>
                  <div><dt className="text-slate-500">This month</dt><dd className="tabular-nums text-slate-800">{num(Number(row.new_this_month) || 0)}</dd></div>
                </dl>
              </details>
            </li>
          ))}
          <li className="px-4 py-3 flex items-center justify-between text-[13px] font-semibold text-slate-900 bg-slate-50">
            <span>Total</span><span className="tabular-nums">{num(sum('open_count'))} open</span>
          </li>
        </ul>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200">
                <th scope="col" className="py-2.5 px-4 text-left text-[12px] font-semibold text-slate-500">Category</th>
                {['Open now', 'New today', 'New this week', 'New this month'].map((h) => (
                  <th key={h} scope="col" className="py-2.5 px-4 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {overview.map((row) => (
                <tr key={row.category}>
                  <td className="py-2.5 px-4"><span className="badge-gray">{catLabel(row.category)}</span></td>
                  <td className="py-2.5 px-4 text-right tabular-nums font-semibold text-slate-900">{num(Number(row.open_count) || 0)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-700">{num(Number(row.new_today) || 0)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-700">{num(Number(row.new_this_week) || 0)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-700">{num(Number(row.new_this_month) || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-900">
                <td className="py-2.5 px-4">Total</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{num(sum('open_count'))}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{num(sum('new_today'))}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{num(sum('new_this_week'))}</td>
                <td className="py-2.5 px-4 text-right tabular-nums">{num(sum('new_this_month'))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Incidents over time (6 months)" bodyClass="p-5">
          {overTime.length === 0 ? <p className="text-[13px] text-slate-500 py-10 text-center">No incidents in the last 6 months</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={overTime} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART.tick, fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: CHART.tick, fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {categories.filter((c) => c.is_active !== false).map((cat, i, arr) => (
                  <Bar key={cat.slug} dataKey={`cat${i + 1}`} name={cat.name} stackId="a" fill={colourOf(cat.color).hex}
                    radius={i === arr.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
        <SectionCard title="Average time to close (hours, last 7 days)" bodyClass="p-5">
          {closeTime.length === 0 ? <p className="text-[13px] text-slate-500 py-10 text-center">No incidents were resolved this week</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={closeTime} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="day" tick={{ fill: CHART.tick, fontSize: 11 }} />
                <YAxis tick={{ fill: CHART.tick, fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="avg_hours" name="Average hours" fill={CHART.bar} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Leaderboard title="Staff with the most incidents (30 days)" rows={byStaff} empty="No incidents linked to staff in the last 30 days" />
        <Leaderboard title="Clients with the most open incidents" rows={byClient} empty="No open incidents linked to clients" />
      </div>
    </div>
  );
}

function CumulativeSection({ stats, categories }) {
  const { overTime = [] } = stats;
  let running = 0;
  const cumData = overTime.map((row) => { running += Number(row.total) || 0; return { month: row.month, total: Number(row.total) || 0, cumulative: running }; });
  const cats = categories.filter((c) => c.is_active !== false);
  return (
    <div className="space-y-5">
      <SectionCard title="Cumulative incidents" bodyClass="p-5">
        {cumData.length === 0 ? <p className="text-[13px] text-slate-500 py-10 text-center">No incidents in the last 6 months</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cumData} margin={{ top: 0, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="month" tick={{ fill: CHART.tick, fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: CHART.tick, fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="total" name="In the month" stroke={CHART.bar} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="cumulative" name="Running total" stroke={CHART.ink} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </SectionCard>
      <SectionCard title="Monthly breakdown">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200">
                <th scope="col" className="py-2.5 px-4 text-left text-[12px] font-semibold text-slate-500">Month</th>
                {cats.map((c) => <th key={c.slug} scope="col" className="py-2.5 px-4 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap">{c.name}</th>)}
                <th scope="col" className="py-2.5 px-4 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap">In the month</th>
                <th scope="col" className="py-2.5 px-4 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap">Running total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {overTime.length === 0 ? (
                <tr><td colSpan={cats.length + 3} className="py-8 text-center text-slate-500">No incidents in the last 6 months</td></tr>
              ) : overTime.map((row, i) => (
                <tr key={row.month || i}>
                  <td className="py-2.5 px-4 font-medium text-slate-900 whitespace-nowrap">{row.month}</td>
                  {cats.map((c, ci) => <td key={c.slug} className="py-2.5 px-4 text-right tabular-nums text-slate-700">{num(Number(row[`cat${ci + 1}`]) || 0)}</td>)}
                  <td className="py-2.5 px-4 text-right tabular-nums font-semibold text-slate-900">{num(Number(row.total) || 0)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-900">{num(cumData[i] ? cumData[i].cumulative : 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

function ByStaffSection() {
  const [range, setRange] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setLoading(true); setError(false);
    api.get(`/incidents/by-staff?dateRange=${range}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const chart = (title, rows, keys) => (
    <SectionCard key={title} title={title} bodyClass="p-5">
      {rows.length === 0 ? <p className="text-[13px] text-slate-500 py-8 text-center">No incidents linked to staff in this period</p> : (
        <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 34)}>
          <BarChart data={rows.map((r) => ({ ...r, total: Number(r.total) || 0, open_count: Number(r.open_count) || 0 }))} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: CHART.tick, fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#374151', fontSize: 11 }} width={120} />
            <Tooltip contentStyle={tooltipStyle} />
            {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {keys.map((k) => <Bar key={k.k} dataKey={k.k} name={k.name} fill={k.fill} radius={[0, 3, 3, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[13px] text-slate-600">Period</span>
        <Tabs variant="segmented" size="sm" ariaLabel="Period" value={range} onChange={setRange}
          tabs={[{ id: '7', label: '7 days' }, { id: '30', label: '30 days' }, { id: '90', label: '90 days' }]} />
      </div>
      {loading ? <div className="bg-white border border-slate-200 rounded-xl"><TableSkeleton rows={5} cols={3} /></div>
        : error ? <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message="We couldn't load incidents by staff. Check your connection, then try again." onRetry={load} /></div>
        : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {chart(`Incidents in the last ${range} days`, (data && data.toDate) || [], [{ k: 'total', name: 'All', fill: CHART.bar }, { k: 'open_count', name: 'Still open', fill: CHART.open }])}
            {chart('Open right now', (data && data.asAtNow) || [], [{ k: 'total', name: 'Open', fill: CHART.open }])}
          </div>
        )}
    </div>
  );
}

function ByClientSection({ rows, loading, error, onRetry, categories, now, handlers }) {
  const groups = useMemo(() => {
    const m = new Map();
    rows.filter((r) => OPEN_STATUSES.includes(r.status)).forEach((r) => {
      const key = r.client_id || 'none';
      if (!m.has(key)) m.set(key, { name: r.client_name || 'No client recorded', items: [] });
      m.get(key).items.push(r);
    });
    return [...m.values()].sort((a, b) => b.items.length - a.items.length);
  }, [rows]);
  if (loading) return <div className="bg-white border border-slate-200 rounded-xl"><TableSkeleton rows={6} cols={5} /></div>;
  if (error) return <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message="We couldn't load open incidents. Check your connection, then try again." onRetry={onRetry} /></div>;
  if (!groups.length) return <div className="bg-white border border-slate-200 rounded-xl"><EmptyState icon={Users} title="No open incidents" hint="Open incidents are grouped here by client." /></div>;
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <SectionCard key={g.name} title={g.name} actions={<span className="badge-yellow tabular-nums">{num(g.items.length)} open</span>}>
          <DataTable label={`Open incidents for ${g.name}`} rows={g.items} stickyHeader={false} onRowClick={handlers.onOpen}
            columns={incidentColumns({ categories, now, ...handlers, showClient: false })} />
        </SectionCard>
      ))}
    </div>
  );
}

function InsightsTab({ stats, loading, error, onRetry, categories, openRows, openLoading, openError, onRetryOpen, now, handlers }) {
  const [view, setView] = useUrlTab('view', 'overview', INSIGHT_VIEWS.map((v) => v.id));
  const needsStats = view === 'overview' || view === 'cumulative';
  return (
    <div className="space-y-4">
      <Tabs variant="segmented" ariaLabel="Insights" tabs={INSIGHT_VIEWS} value={view} onChange={setView} className="max-w-full overflow-x-auto" />
      {needsStats && loading ? <div className="bg-white border border-slate-200 rounded-xl"><TableSkeleton rows={6} cols={5} /></div>
        : needsStats && (error || !stats) ? <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message="We couldn't load incident insights. Check your connection, then try again." onRetry={onRetry} /></div>
        : view === 'overview' ? <OverviewSection stats={stats} categories={categories} />
        : view === 'cumulative' ? <CumulativeSection stats={stats} categories={categories} />
        : view === 'staff' ? <ByStaffSection />
        : <ByClientSection rows={openRows} loading={openLoading} error={openError} onRetry={onRetryOpen} categories={categories} now={now} handlers={handlers} />}
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────
const ROLE_LABELS = { team_lead: 'Team lead', risk_manager: 'Risk manager', general_manager: 'General manager' };
const ESCALATION = [
  { priority: 'critical', desc: 'Escalated to the team lead straight away' },
  { priority: 'high', desc: 'Escalated to the risk manager within 4 hours' },
  { priority: 'medium', desc: 'Reviewed within 24 hours by the assigned staff member' },
  { priority: 'low', desc: 'Reviewed in the weekly cycle' },
];

function SettingsTab({ categories, onCategoriesChanged }) {
  const confirm = useConfirm();
  const [teamSettings, setTeamSettings] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editCat, setEditCat] = useState(null); // null = closed, {} = new, {id,…} = edit
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    setLoadError(false);
    Promise.all([api.get('/incidents/settings'), api.get('/staff')])
      .then(([s, u]) => {
        setTeamSettings(s.data || []);
        setUsers(Array.isArray(u.data) ? u.data : ((u.data && u.data.staff) || []));
        setLoaded(true);
      })
      .catch(() => setLoadError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateRole = async (role, userId) => {
    setSaving((v) => ({ ...v, [role]: true }));
    try {
      await api.put('/incidents/settings', { role, userId: userId || null });
      setTeamSettings((prev) => prev.map((s) => (s.role === role ? { ...s, user_id: userId } : s)));
      toast.success(`${ROLE_LABELS[role] || 'Role'} updated`);
    } catch { toast.error('Could not update the role'); }
    finally { setSaving((v) => ({ ...v, [role]: false })); }
  };

  const handleDeleteCategory = async (cat) => {
    if (!(await confirm(`"${cat.name}" is removed from the category list. A category that incidents still use can't be deleted.`,
      { title: `Delete ${cat.name}?`, confirmLabel: 'Delete category', danger: true }))) return;
    setDeletingId(cat.id);
    try {
      await api.delete(`/incidents/categories/${cat.id}`);
      toast.success('Category deleted');
      onCategoriesChanged();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'Could not delete the category');
    } finally { setDeletingId(null); }
  };

  const closeCat = useCallback(() => setEditCat(null), []);

  if (loadError) return <div className="bg-white border border-slate-200 rounded-xl max-w-3xl"><ErrorState message="We couldn't load incident settings. Check your connection, then try again." onRetry={load} /></div>;
  if (!loaded) return <div className="bg-white border border-slate-200 rounded-xl max-w-3xl"><TableSkeleton rows={6} cols={3} /></div>;

  return (
    <div className="max-w-3xl space-y-5">
      <SectionCard title="Incident categories" icon={Tag}
        actions={<button type="button" onClick={() => setEditCat({})} className="btn-secondary btn-sm"><Plus size={14} /> New category</button>}>
        {categories.length === 0 ? (
          <EmptyState icon={Tag} title="No categories yet" hint="Categories group incidents in the register and the insights." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {categories.map((cat) => (
              <li key={cat.id} className="flex items-center gap-3 px-5 py-3">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colourOf(cat.color).hex }} aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-900 truncate">{cat.name}{cat.is_active === false && <span className="text-slate-500 font-normal"> · inactive</span>}</p>
                  <p className="text-[12px] text-slate-500 truncate">{cat.description || 'No description'}</p>
                </div>
                <button type="button" onClick={() => setEditCat(cat)} aria-label={`Edit ${cat.name}`}
                  className="p-2.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors duration-150">
                  <Pencil size={16} />
                </button>
                <button type="button" onClick={() => handleDeleteCategory(cat)} disabled={deletingId === cat.id} aria-label={`Delete ${cat.name}`}
                  className="p-2.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors duration-150 disabled:opacity-50">
                  {deletingId === cat.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Incident team" icon={Users}>
        <p className="px-5 pt-4 text-[13px] text-slate-500">Who each escalation goes to.</p>
        <ul className="divide-y divide-slate-100">
          {teamSettings.map((s) => (
            <li key={s.role} className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-center">
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-900">{ROLE_LABELS[s.role] || s.role}</p>
                <p className="text-[12px] text-slate-500">{s.user_name || 'Not assigned'}</p>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor={`incident-role-${s.role}`} className="sr-only">{ROLE_LABELS[s.role] || s.role}</label>
                <select id={`incident-role-${s.role}`} value={s.user_id || ''} onChange={(e) => updateRole(s.role, e.target.value)}
                  className="input-field sm:!w-60" disabled={saving[s.role]}>
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
                {saving[s.role] && <Loader2 size={15} className="animate-spin text-slate-400" />}
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Escalation by priority" icon={AlertTriangle}>
        <ul className="divide-y divide-slate-100">
          {ESCALATION.map((row) => (
            <li key={row.priority} className="flex items-center gap-3 px-5 py-3">
              <span className="w-20 shrink-0"><PriorityPill priority={row.priority} /></span>
              <span className="text-[13px] text-slate-700">{row.desc}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      {editCat !== null && (
        <CategoryDialog category={editCat && editCat.id ? editCat : null} onClose={closeCat} onSaved={onCategoriesChanged} />
      )}
    </div>
  );
}

// ── AI root-cause analysis ───────────────────────────────────────────────────
const RISK = { high: { label: 'High', tone: 'danger' }, medium: { label: 'Medium', tone: 'warning' }, low: { label: 'Low', tone: 'success' } };
const REC_PRIORITY = {
  immediate: { label: 'Immediate', tone: 'danger' },
  short_term: { label: 'Short term', tone: 'warning' },
  long_term: { label: 'Long term', tone: 'info' },
};

function RootCauseDialog({ loading, result, onClose }) {
  return (
    <Dialog open onClose={onClose} size="lg" title="Root cause analysis" description="Drafted by AI from the last 30 days of incident patterns. Check it before acting on it.">
      {loading ? (
        <div className="space-y-3 py-2" aria-busy="true" aria-label="Analysing incident patterns">
          <div className="skeleton h-4 w-1/3" /><div className="skeleton h-4 w-full" /><div className="skeleton h-4 w-5/6" /><div className="skeleton h-4 w-2/3" />
        </div>
      ) : result ? (
        <div className="space-y-5">
          {result.riskLevel && (
            <StatusPill status={result.riskLevel} map={RISK} label={`Overall risk: ${(RISK[result.riskLevel] || { label: result.riskLevel }).label}`} />
          )}
          {result.summary && <p className="text-[13.5px] text-slate-700 leading-relaxed">{result.summary}</p>}
          {result.rootCauses && result.rootCauses.length > 0 && (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-900 mb-1.5">Root causes</h3>
              <ul className="list-disc pl-5 space-y-1 text-[13px] text-slate-700">{result.rootCauses.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          {result.contributingFactors && result.contributingFactors.length > 0 && (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-900 mb-1.5">Contributing factors</h3>
              <ul className="list-disc pl-5 space-y-1 text-[13px] text-slate-700">{result.contributingFactors.map((f, i) => <li key={i}>{f}</li>)}</ul>
            </div>
          )}
          {result.recommendations && result.recommendations.length > 0 && (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-900 mb-2">Recommendations</h3>
              <ul className="space-y-2">
                {result.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200">
                    <StatusPill status={r.priority} map={REC_PRIORITY} className="shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-slate-800">{r.action}</p>
                      {r.owner && <p className="text-[12px] text-slate-500 mt-0.5">Owner: {r.owner}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Incidents() {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useUrlTab('tab', 'queue', TAB_IDS);
  const [now, setNow] = useState(() => Date.now());

  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [ndisStats, setNdisStats] = useState(null);

  const [openRows, setOpenRows] = useState([]);
  const [openLoading, setOpenLoading] = useState(true);
  const [openError, setOpenError] = useState(false);
  const [ndisRows, setNdisRows] = useState([]);
  const [ndisLoading, setNdisLoading] = useState(true);
  const [ndisError, setNdisError] = useState(false);

  const [allRows, setAllRows] = useState([]);
  const [allLoading, setAllLoading] = useState(true);
  const [allError, setAllError] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [clientOptions, setClientOptions] = useState([]);

  const [formFor, setFormFor] = useState(null); // null | 'new' | incident
  const [viewIncident, setViewIncident] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [patternDismissed, setPatternDismissed] = useState(false);
  const [rootCause, setRootCause] = useState(null);
  const [rootCauseOpen, setRootCauseOpen] = useState(false);
  const [loadingRootCause, setLoadingRootCause] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // The notification clocks move on their own.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  // Old numbered tab links (?tab=7) land on the matching view.
  useEffect(() => {
    const raw = searchParams.get('tab');
    if (!raw || !/^\d+$/.test(raw)) return;
    const n = Number(raw);
    const next = new URLSearchParams(searchParams);
    const t = LEGACY_TAB[n];
    if (!t || t === 'queue') next.delete('tab'); else next.set('tab', t);
    if (LEGACY_VIEW[n]) next.set('view', LEGACY_VIEW[n]);
    if (n === 5) setStatusFilter('resolved');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Search runs on the server, so wait for a pause in typing.
  useEffect(() => { const t = setTimeout(() => setSearch(searchDraft.trim()), 300); return () => clearTimeout(t); }, [searchDraft]);

  const loadCategories = useCallback(() => {
    api.get('/incidents/categories').then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);
  const loadStats = useCallback(() => {
    setStatsLoading(true); setStatsError(false);
    api.get('/incidents/stats').then((r) => setStats(r.data)).catch(() => setStatsError(true)).finally(() => setStatsLoading(false));
    api.get('/incidents/ndis-stats').then((r) => setNdisStats(r.data)).catch(() => {});
  }, []);
  const loadPatterns = useCallback(() => {
    api.get('/incidents/patterns').then((r) => setPatterns(r.data)).catch(() => {});
  }, []);
  const loadOpen = useCallback(() => {
    setOpenLoading(true); setOpenError(false);
    api.get('/incidents', { params: { status: OPEN_STATUSES.join(',') } })
      .then((r) => setOpenRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOpenError(true))
      .finally(() => setOpenLoading(false));
  }, []);
  const loadNdis = useCallback(() => {
    setNdisLoading(true); setNdisError(false);
    api.get('/incidents', { params: { ndis: 'true' } })
      .then((r) => setNdisRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNdisError(true))
      .finally(() => setNdisLoading(false));
  }, []);
  const loadAll = useCallback(() => {
    setAllLoading(true); setAllError(false);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (search) params.search = search;
    api.get('/incidents', { params })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setAllRows(list);
        // The client filter lists the clients that actually have incidents (from the unfiltered list).
        if (!statusFilter && !priorityFilter && !categoryFilter && !clientFilter && !search) {
          const seen = new Map();
          list.forEach((i) => { if (i.client_id && i.client_name && !seen.has(i.client_id)) seen.set(i.client_id, i.client_name); });
          setClientOptions([...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch(() => setAllError(true))
      .finally(() => setAllLoading(false));
  }, [statusFilter, priorityFilter, categoryFilter, clientFilter, search]);

  useEffect(() => { loadCategories(); loadStats(); loadPatterns(); loadOpen(); loadNdis(); }, [loadCategories, loadStats, loadPatterns, loadOpen, loadNdis]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshAll = useCallback(() => { loadAll(); loadOpen(); loadNdis(); loadStats(); loadPatterns(); }, [loadAll, loadOpen, loadNdis, loadStats, loadPatterns]);
  const handleSaved = refreshAll;

  // The queue: every open incident, plus any reportable incident the Commission hasn't been told
  // about whatever its status.
  const queueRows = useMemo(() => {
    const m = new Map(openRows.map((r) => [r.id, r]));
    ndisRows.forEach((r) => { if (needsNotifying(ndisClock(r, now)) && !m.has(r.id)) m.set(r.id, r); });
    return [...m.values()].sort(byUrgency(now));
  }, [openRows, ndisRows, now]);

  const ndisSorted = useMemo(() => [...ndisRows].sort(byUrgency(now)), [ndisRows, now]);

  const notifyStats = useMemo(() => {
    const waiting = ndisRows.map((r) => ndisClock(r, now)).filter(needsNotifying);
    const oldest = waiting.reduce((mx, c) => Math.max(mx, c.ageMs || 0), 0);
    const d = Math.floor(oldest / DAY);
    return { count: waiting.length, oldestText: d <= 0 ? 'less than a day' : plural(d, 'day') };
  }, [ndisRows, now]);

  const runRootCause = async () => {
    if (!patterns) return;
    setLoadingRootCause(true);
    setRootCauseOpen(true);
    setRootCause(null);
    try {
      const { data } = await api.post('/ai/incident-root-cause', { patterns });
      // Safety net: if the analysis ever comes back as a raw JSON string (or stuffed into
      // .summary), re-parse it so the dialog shows formatted sections, not JSON.
      let rc = data;
      const blob = typeof data === 'string' ? data : (data && typeof data.summary === 'string' ? data.summary : '');
      if (blob && /"rootCauses"\s*:/.test(blob)) {
        try {
          const m = blob.replace(/```json\s*|```/gi, '').match(/\{[\s\S]*\}/);
          if (m) rc = JSON.parse(m[0]);
        } catch { /* keep original */ }
      }
      setRootCause(rc);
    } catch { toast.error('The root cause analysis failed. Try again.'); setRootCauseOpen(false); }
    finally { setLoadingRootCause(false); }
  };

  const openView = useCallback((inc) => setViewIncident(inc), []);
  const openEdit = useCallback((inc) => { setViewIncident(null); setFormFor(inc); }, []);
  const openNew = useCallback(() => setFormFor('new'), []);
  const closeForm = useCallback(() => setFormFor(null), []);
  const closeView = useCallback(() => setViewIncident(null), []);
  const closeRootCause = useCallback(() => setRootCauseOpen(false), []);

  const handleArchive = useCallback(async (inc) => {
    const ok = await confirm('It moves out of every incident list here. Archived incidents stay on record.', {
      title: `Archive ${inc.ticket_name ? `"${inc.ticket_name}"` : 'this incident'}?`, confirmLabel: 'Archive incident', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/incidents/${inc.id}`);
      toast.success('Incident archived');
      setViewIncident(null);
      refreshAll();
    } catch { toast.error('Could not archive the incident'); }
  }, [confirm, refreshAll]);

  const handlers = useMemo(() => ({ onOpen: openView, onEdit: openEdit, onArchive: handleArchive }), [openView, openEdit, handleArchive]);

  // Downloads cover the "All incidents" list with its current filters, as before.
  const downloadPDF = () => {
    if (!allRows.length) { toast.error('There are no incidents in this list to download'); return; }
    exportIncidentsPDF(allRows, categories);
    toast.success(`PDF ready: ${plural(allRows.length, 'incident')}, oldest first`);
  };
  const downloadDocx = async () => {
    if (!allRows.length) { toast.error('There are no incidents in this list to download'); return; }
    setDownloading(true);
    try { await exportIncidentsDocx(allRows, categories); toast.success(`Word document ready: ${plural(allRows.length, 'incident')}, oldest first`); }
    catch { toast.error('Could not create the Word document'); }
    finally { setDownloading(false); }
  };
  const downloadXlsx = async () => {
    if (!allRows.length) { toast.error('There are no incidents in this list to download'); return; }
    setDownloading(true);
    try { await exportIncidentRegisterXlsx(allRows, categories); toast.success(`Register ready: ${plural(allRows.length, 'incident')}`); }
    catch { toast.error('Could not create the Excel register'); }
    finally { setDownloading(false); }
  };

  const filtersOn = !!(statusFilter || priorityFilter || categoryFilter || clientFilter || searchDraft.trim());
  const clearFilters = () => { setStatusFilter(''); setPriorityFilter(''); setCategoryFilter(''); setClientFilter(''); setSearchDraft(''); setSearch(''); };

  const showPatterns = !patternDismissed && patterns
    && ((patterns.byType || []).length > 0 || (patterns.byClient || []).length > 0 || (patterns.byLocation || []).length > 0);

  const tabs = TABS.map((t) => (t.id === 'queue' ? { ...t, count: openLoading ? undefined : queueRows.length }
    : t.id === 'ndis' ? { ...t, count: ndisLoading || !notifyStats.count ? undefined : notifyStats.count } : t));

  const selectCls = 'input-field md:!w-auto md:min-w-[150px]';

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 lg:flex-1">
          <label htmlFor="incident-view" className="sr-only">View</label>
          <select id="incident-view" className="input-field sm:hidden" value={tab} onChange={(e) => setTab(e.target.value)}>
            {tabs.map((t) => <option key={t.id} value={t.id}>{t.label}{t.count ? ` (${t.count})` : ''}</option>)}
          </select>
          <Tabs className="hidden sm:flex" tabs={tabs} value={tab} onChange={setTab} ariaLabel="Incident views" />
        </div>
        <div className="flex items-center gap-2 flex-wrap lg:pb-1.5">
          <button type="button" onClick={refreshAll} className="btn-secondary !px-2.5" aria-label="Refresh incidents"><RefreshCw size={15} /></button>
          <DownloadMenu busy={downloading} onPdf={downloadPDF} onDocx={downloadDocx} onXlsx={downloadXlsx} />
          <button type="button" onClick={openNew} className="btn-primary"><Plus size={16} /> Report incident</button>
        </div>
      </div>

      {showPatterns && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" role="status">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-amber-900 mb-1.5">Patterns in the last 30 days</p>
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {(patterns.byType || []).map((p) => (
                <span key={`t-${p.label}`} className="text-[12px] bg-white/70 border border-amber-200 text-amber-900 px-2 py-0.5 rounded-full">{num(p.count)} × {typeLabel(p.label) || 'No type'}</span>
              ))}
              {(patterns.byClient || []).map((p) => (
                <span key={`c-${p.label}`} className="text-[12px] bg-white/70 border border-amber-200 text-amber-900 px-2 py-0.5 rounded-full">{plural(p.count, 'incident')} for {p.label}</span>
              ))}
              {(patterns.byLocation || []).map((p) => (
                <span key={`l-${p.label}`} className="text-[12px] bg-white/70 border border-amber-200 text-amber-900 px-2 py-0.5 rounded-full">{num(p.count)} × at {p.label}</span>
              ))}
            </div>
            <button type="button" onClick={runRootCause} disabled={loadingRootCause} className="btn-secondary btn-sm">
              {loadingRootCause ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}Analyse root causes
            </button>
          </div>
          <button type="button" onClick={() => setPatternDismissed(true)} aria-label="Dismiss patterns"
            className="p-2 -m-1 rounded-lg text-amber-700 hover:bg-amber-100 shrink-0"><X size={15} /></button>
        </div>
      )}

      {tab === 'queue' && (
        <QueueTab rows={queueRows} loading={openLoading || ndisLoading}
          error={openError || ndisError ? "We couldn't load the whole incident queue. Check your connection, then try again." : null}
          onRetry={() => { loadOpen(); loadNdis(); }} notifyStats={notifyStats} ndisFailed={ndisError}
          categories={categories} now={now} handlers={handlers} onNew={openNew} />
      )}

      {tab === 'all' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
              <SearchInput value={searchDraft} onChange={setSearchDraft} placeholder="Search by title" className="w-full md:w-64" />
              <label htmlFor="incident-priority" className="sr-only">Priority</label>
              <select id="incident-priority" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className={selectCls}>
                <option value="">All priorities</option>
                {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
              <label htmlFor="incident-category" className="sr-only">Category</label>
              <select id="incident-category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className={selectCls}>
                <option value="">All categories</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <label htmlFor="incident-client" className="sr-only">Client</label>
              <select id="incident-client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className={selectCls}>
                <option value="">All clients</option>
                {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {filtersOn && <button type="button" onClick={clearFilters} className="btn-ghost btn-sm self-start md:self-auto">Clear filters</button>}
              <span className="md:ml-auto text-[13px] text-slate-500 tabular-nums" aria-live="polite">{allLoading ? 'Loading…' : plural(allRows.length, 'incident')}</span>
            </div>
            <FilterChips multi={false} ariaLabel="Filter by status" value={statusFilter || null} onChange={(v) => setStatusFilter(v || '')}
              options={[{ value: null, label: 'All statuses' }, ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]} />
          </div>
          <DataTable
            label="All incidents"
            columns={incidentColumns({ categories, now, ...handlers })}
            rows={allRows}
            loading={allLoading}
            error={allError ? "We couldn't load incidents. Check your connection, then try again." : null}
            onRetry={loadAll}
            stickyHeader={false}
            onRowClick={openView}
            empty={filtersOn ? {
              icon: ClipboardList, title: 'No incidents match these filters',
              action: <button type="button" onClick={clearFilters} className="btn-secondary btn-sm">Clear filters</button>,
            } : {
              icon: ClipboardList, title: 'No incidents recorded yet',
              hint: 'Report an incident as soon as it happens so the follow-up and any NDIS notification are tracked.',
              action: <button type="button" onClick={openNew} className="btn-primary btn-sm"><Plus size={14} /> Report incident</button>,
            }}
          />
        </div>
      )}

      {tab === 'ndis' && (
        <NdisTab rows={ndisSorted} loading={ndisLoading} error={ndisError ? "We couldn't load reportable incidents. Check your connection, then try again." : null}
          onRetry={loadNdis} ndisStats={ndisStats} notifyStats={notifyStats} now={now} handlers={handlers} />
      )}

      {tab === 'insights' && (
        <InsightsTab stats={stats} loading={statsLoading} error={statsError} onRetry={loadStats} categories={categories}
          openRows={openRows} openLoading={openLoading} openError={openError} onRetryOpen={loadOpen} now={now} handlers={handlers} />
      )}

      {tab === 'settings' && <SettingsTab categories={categories} onCategoriesChanged={loadCategories} />}

      {formFor && (
        <IncidentForm incident={formFor === 'new' ? null : formFor} categories={categories} onClose={closeForm} onSaved={handleSaved} />
      )}
      {viewIncident && (
        <IncidentDrawer incident={viewIncident} categories={categories} now={now} onClose={closeView}
          onEdit={() => openEdit(viewIncident)} onArchive={() => handleArchive(viewIncident)} />
      )}
      {rootCauseOpen && <RootCauseDialog loading={loadingRootCause} result={rootCause} onClose={closeRootCause} />}
    </div>
  );
}
